import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import supabase from '../lib/supabaseClient';

export default function ExtensionAuth() {
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get extension origin from URL (e.g., chrome-extension://[id])
  const extensionOrigin = searchParams.get('origin');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    checkSessionAndAuthorize();
  }, []);

  const checkSessionAndAuthorize = async () => {
    try {
      // Validate that we have an extension origin
      if (!extensionOrigin) {
        throw new Error('Extension origin is required. Extension must pass ?origin=chrome-extension://[id] in URL.');
      }

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        throw sessionError;
      }

      if (!session) {
        // Not logged in, redirect to login with return URL
        setStatus('not_logged_in');
        return;
      }

      // User is logged in, show authorize button
      setStatus('ready');

    } catch (err) {
      console.error('Extension auth error:', err);
      setError(err.message);
      setStatus('error');
    }
  };

  const handleAuthorize = async () => {
    setStatus('authorizing');

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Session expired');
      }

      if (!extensionOrigin) {
        throw new Error('Extension origin is missing');
      }

      // Send token to extension via postMessage
      // 🔒 SECURITY: Using specific extension origin instead of wildcard
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'EXTENSION_AUTH_COMPLETE',
            accessToken: session.access_token,  // plaintext token
            user: {
              id: session.user.id,
              email: session.user.email
            }
          },
          extensionOrigin  // chrome-extension://[id] passed by extension
        );

        console.log('✅ Auth token sent to extension via postMessage');
      } else {
        throw new Error('No window.opener found - this page must be opened by the extension');
      }

      setStatus('success');

      // Auto-close window after showing success
      setTimeout(() => {
        window.close();
      }, 2000);

    } catch (err) {
      console.error('Authorization error:', err);
      setError(err.message);
      setStatus('error');
    }
  };

  const handleLogin = () => {
    // Redirect to login, preserving origin and session_id in return URL
    const params = new URLSearchParams();
    if (extensionOrigin) params.append('origin', extensionOrigin);
    if (sessionId) params.append('session_id', sessionId);

    const returnTo = `/extension-auth?${params.toString()}`;
    navigate('/login', { state: { returnTo } });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        {/* Logo */}
        <img
          src="/newSurFoxLogo1.png"
          alt="SurFox AI"
          className="h-14 mx-auto mb-6 object-contain"
        />

        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Checking your session...</p>
          </>
        )}

        {status === 'not_logged_in' && (
          <>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Login Required
            </h2>
            <p className="text-gray-600 mb-6">
              Please log in to your SurFox account to connect the Chrome extension.
            </p>
            <button
              onClick={handleLogin}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition"
            >
              Log In to SurFox
            </button>
          </>
        )}

        {status === 'ready' && (
          <>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Connect Chrome Extension
            </h2>
            <p className="text-gray-600 mb-6">
              Click below to authorize the SurFox Lead Pusher extension to access your account.
            </p>
            <button
              onClick={handleAuthorize}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition"
            >
              🔗 Authorize Extension
            </button>
          </>
        )}

        {status === 'authorizing' && (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Connecting extension...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Connected!
            </h2>
            <p className="text-gray-600">
              You can close this window and start using the extension.
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-red-500 text-5xl mb-4">✕</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Connection Failed
            </h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-700 transition"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}